import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Pencil, X, Check, Loader2, RotateCw } from "lucide-react";
import { api } from "@/lib/api";

export const toLocalInput = (iso) => {
  const d = iso ? new Date(iso) : new Date(Date.now() + 60 * 60 * 1000);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
export const fmtWhen = (iso) => new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });

const STATUS = {
  PENDING: ["Programmé", "bg-blue-50 text-blue-700 border-blue-200"],
  SENDING: ["Envoi en cours", "bg-amber-50 text-amber-700 border-amber-200"],
  SENT: ["Envoyé", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  FAILED: ["Échec", "bg-red-50 text-red-700 border-red-200"],
  CANCELLED: ["Annulé", "bg-zinc-50 text-zinc-500 border-zinc-200"],
};

function Row({ r, onChanged, highlight }) {
  const [edit, setEdit] = useState(false);
  const [when, setWhen] = useState(toLocalInput(r.scheduled_at));
  const [busy, setBusy] = useState(false);
  const [label, cls] = STATUS[r.status] || [r.status, ""];
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); setEdit(false); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  const save = () => run(() => api.patch(`/scheduled-quote-emails/${r.schedule_id}`, { scheduled_at: new Date(when).toISOString() }), "Envoi reprogrammé");
  const retry = () => run(() => api.post(`/scheduled-quote-emails/${r.schedule_id}/retry`), `Devis renvoyé à ${r.to}`);
  const cancel = () => window.confirm("Annuler cet envoi programmé ?") && run(() => api.delete(`/scheduled-quote-emails/${r.schedule_id}`), "Envoi programmé annulé");
  return (
    <div data-testid={`scheduled-email-${r.schedule_id}`} className={`flex items-center gap-2 py-1.5 text-xs border-b border-blue-100 last:border-0 ${highlight ? "bg-red-50 ring-1 ring-red-300 rounded px-1" : ""}`}>
      <span className={`px-1.5 py-0.5 rounded border text-[10px] font-medium ${cls}`} data-testid={`scheduled-email-status-${r.schedule_id}`}>{label}</span>
      {edit ? (
        <input type="datetime-local" value={when} min={toLocalInput(new Date().toISOString())} onChange={(e) => setWhen(e.target.value)}
          data-testid={`scheduled-email-edit-input-${r.schedule_id}`} className="h-7 border border-zinc-200 rounded px-1.5 text-xs bg-white" />
      ) : (
        <span className="font-mono text-zinc-800" data-testid={`scheduled-email-when-${r.schedule_id}`}>{fmtWhen(r.status === "SENT" && r.sent_at ? r.sent_at : r.scheduled_at)}</span>
      )}
      <span className="flex-1 min-w-0 truncate text-zinc-600">→ {r.to}{r.cc?.length ? ` (+${r.cc.length} cc)` : ""}{r.error ? ` — ${r.error}` : ""}</span>
      {r.status === "FAILED" && (
        <button type="button" onClick={retry} disabled={busy} data-testid={`scheduled-email-retry-${r.schedule_id}`}
          className="inline-flex items-center gap-1 px-2 h-7 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCw size={12} />} Réessayer
        </button>
      )}
      {r.status === "PENDING" && (edit ? (
        <>
          <button type="button" onClick={save} disabled={busy} data-testid={`scheduled-email-save-${r.schedule_id}`} className="p-1 rounded hover:bg-emerald-50 text-emerald-700">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
          </button>
          <button type="button" onClick={() => setEdit(false)} className="p-1 rounded hover:bg-zinc-100" data-testid={`scheduled-email-edit-cancel-${r.schedule_id}`}><X size={13} /></button>
        </>
      ) : (
        <>
          <button type="button" onClick={() => setEdit(true)} title="Modifier la date/heure" data-testid={`scheduled-email-edit-${r.schedule_id}`} className="p-1 rounded hover:bg-blue-100"><Pencil size={12} /></button>
          <button type="button" onClick={cancel} title="Annuler l'envoi" data-testid={`scheduled-email-cancel-${r.schedule_id}`} className="p-1 rounded hover:bg-red-50 text-red-600"><X size={13} /></button>
        </>
      ))}
    </div>
  );
}

export default function ScheduledQuoteEmails({ simulationId, refreshKey, onChanged, highlightId }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get(`/simulations/${simulationId}/scheduled-quote-emails`).then((r) => setRows(r.data?.scheduled || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, [simulationId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!rows.length) return null;
  return (
    <div data-testid="scheduled-quote-emails" className="mt-3 rounded-md border border-blue-200 bg-blue-50/40 px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.2em] text-blue-700 font-mono flex items-center gap-1.5 mb-1">
        <CalendarClock size={12} /> Envois programmés du devis
      </div>
      {rows.map((r) => <Row key={r.schedule_id} r={r} highlight={r.schedule_id === highlightId && r.status === "FAILED"} onChanged={() => { load(); onChanged && onChanged(); }} />)}
    </div>
  );
}
